














































































































































import {
  Vue,
  Component,
  Watch,
} from "@/shared/vuepack";
import _ from "lodash";
import moment from "moment";
import { formatCurrency } from "@/shared/currency";
import { Validator } from "vee-validate";
import {
  ScheduledPersonalContribution,
  BankAccountDefaultsRequest,
  ContributionScheduleType,
  ContributionScheduleStatus,
  FundingOption
} from "@/shared/types";
import SchedulePersonalContributionConfirmModal from "@/employee/components/contributionsV2/SchedulePersonalContributionConfirmModal.vue";
import SchedulePersonalContributionCancelModal from "@/employee/components/contributionsV2/SchedulePersonalContributionCancelModal.vue";
import ContributionsProgressBar from "@/employee/components/shared/ContributionsProgressBar.vue";
import { formatDateString } from "@/shared/dateString";
import { useUserStore } from "@/shared/store/user";
import { useScheduledPersonalContributionStore } from "@/employee/store/scheduledPersonalContribution";
import { useBankAccountStore } from "@/employee/store/bankAccount";
import { usePartnerPortalStore } from "@/employee/store/partnerPortal";
import { useHsaStore } from "@/shared/store/hsa";
import { useHsaLimitStore } from "@/shared/store/hsalimit";
import { useHsaTotalStore } from "@/shared/store/hsaTotal";

const emptyScheduledPersonalContribution: ScheduledPersonalContribution = {
  amount: 0,
  contributionScheduleType: ContributionScheduleType.None,
  dateScheduledStart: moment().format("DD, MMM, YYYYY"),
  status: ContributionScheduleStatus.Active,
  personalContributionScheduleId: 0,
  hasScheduledContributionToday: false,
  nextScheduledContribution: null,
  lastContributionForYear: null
};

@Component({
  components: {
    SchedulePersonalContributionConfirmModal,
    SchedulePersonalContributionCancelModal,
    ContributionsProgressBar
  },
})
export default class SchedulePersonalContributionV2 extends Vue {
  userStore = useUserStore();
  scheduledPersonalContributionStore = useScheduledPersonalContributionStore();
  bankAccountStore = useBankAccountStore();
  partnerPortalStore = usePartnerPortalStore();
  hsaStore = useHsaStore();
  hsaLimitStore = useHsaLimitStore();
  hsaTotalStore = useHsaTotalStore();

  $validator: Validator;

  isEditing = false;
  isSubmitting = false;
  confirmModalVisible = false;
  cancelModalVisible = false;
  defaultScheduledContributionAccountId = 0;
  formModel = { ...emptyScheduledPersonalContribution };
  activePayrollYear = moment().year();

  mounted (): void {
    this.scheduledPersonalContributionStore.getScheduledPersonalContribution();
    this.bankAccountStore.loadLinkedBankAccounts();
    this.partnerPortalStore.getPartnerPortalStatus();
    this.hsaTotalStore.getUserPayrollSummary();
    this.hsaLimitStore.getHsaAccountLimits();
    this.hsaStore.loadTransactionTotals({ year: moment().year() });

    if (this.scheduledPersonalContributionStore.isLoaded) {
      this.initializeFormModel();
    }
  }

  @Watch("scheduledPersonalContributionStore.scheduledPersonalContribution")
  initializeFormModel (): void {
    this.formModel = { ...this.scheduledPersonalContributionStore.scheduledPersonalContribution };
  }

  @Watch("bankAccountStore.defaultScheduledContributionAccount", { deep: true })
  initializeLinkedAccount (): void {
    if (this.bankAccountStore.defaultScheduledContributionAccount) {
      this.defaultScheduledContributionAccountId
          = this.bankAccountStore.defaultScheduledContributionAccount.linkedBankAccountId;
    }
  }

  get widgetDisplayTitle (): string {
    if (this.userStore.isEmployeeAccountType && !this.hasScheduledContributions) {
      return "Contributions";
    }

    return "Schedule Personal Contribution";
  }

  get displayContributionInfo (): boolean {
    if (this.userStore.isEmployeeAccountType && !this.hasScheduledContributions || this.hasLinkedAccounts === false) {
      return false;
    }

    return true;
  }

  get hasUpcomingContributionDate (): boolean {
    return !!this.scheduledPersonalContributionStore.scheduledPersonalContribution &&
        !!this.scheduledPersonalContributionStore.scheduledPersonalContribution.nextScheduledContribution;
  }

  get upcomingContributionAmount (): string {
    if (this.hasUpcomingContributionDate) {
      return formatCurrency(
          this.scheduledPersonalContributionStore.scheduledPersonalContribution.nextScheduledContribution.amount);
    }

    return "";
  }

  get upcomingContributionDate (): string {
    if (this.hasUpcomingContributionDate) {
      return moment.utc(
              this.scheduledPersonalContributionStore.scheduledPersonalContribution.nextScheduledContribution.date)
          .format("dddd, MMMM Do, YYYY");
    }

    return "";
  }

  get hasLastContributionForYear (): boolean {
    return !!this.scheduledPersonalContributionStore.scheduledPersonalContribution &&
        !!this.scheduledPersonalContributionStore.scheduledPersonalContribution.lastContributionForYear;
  }

  get lastContributionForYear (): string {
    if (this.hasLastContributionForYear) {
      return moment.utc(
              this.scheduledPersonalContributionStore.scheduledPersonalContribution.lastContributionForYear.date)
          .format("dddd, MMMM Do, YYYY");
    }

    return "";
  }

  get dataLoaded (): boolean {
    return this.scheduledPersonalContributionStore.isLoaded && this.bankAccountStore.linkedAccountsLoaded;
  }

  get scheduledPersonalContributionTypeSelectOptions () {
    return [
      { text: "1st of the month", value: ContributionScheduleType.FirstOfMonth },
      { text: "Last day of the month", value: ContributionScheduleType.LastOfMonth },
      { text: "1st & 15th of the month", value: ContributionScheduleType.FirstAndMiddleOfMonth }
    ];
  }

  get validateContributionAmount () {
    const maximum = this.hsaTotalStore.maximumRemainingContribution;
    return {
      required: true,
      decimal: 2,
      min_value_usd: 0.01,
      max_value_usd: maximum
    };
  }

  get recurringAmountText (): string {
    if (this.hasScheduledContributions) {
      return formatCurrency(this.formModel.amount);
    }

    return "None";
  }

  get frequencyText (): string {
    if (this.hasScheduledContributions) {
      switch (this.formModel.contributionScheduleType) {
        case ContributionScheduleType.FirstOfMonth:
          return "First of month";
        case ContributionScheduleType.LastOfMonth:
          return "Last of month";
        case ContributionScheduleType.FirstAndMiddleOfMonth:
          return "1st and 15th of month";
      }
    }

    return "None";
  }

  get sourceText (): string {
    if (this.hasScheduledContributions) {
      const selectedAccount = _.find(this.bankAccountStore.verifiedLinkedAccounts,
          x => x.linkedBankAccountId === this.defaultScheduledContributionAccountId);

      if (!selectedAccount) {
        return "None";
      }

      return selectedAccount.bankAccountName + " " + selectedAccount.bankAccountNumber;
    }

    return "None";
  }

  get startDateText (): string {
    if (this.hasScheduledContributions) {
      return formatDateString(this.formModel.dateScheduledStart);
    }

    return "None";
  }

  get hsaLimit () {
    return this.hsaLimitStore.getHsaLimitByYear(moment().year());
  }

  get totalEmployeePayrollContributions (): number {
    return this.totalEmployeePreTaxPayrollContributions;
  }

  get totalEmployeePreTaxPayrollContributions (): number {
    return this.hsaTotalStore.initialAnnualPreTaxContribution;
  }

  get showCancelButton (): boolean {
    return this.scheduledPersonalContributionStore.scheduledPersonalContribution.status ===
        ContributionScheduleStatus.Active;
  }

  get hasLinkedAccounts (): boolean {
    return this.linkedAccountSelectOptions.length > 0;
  }

  get employeePersonalContributions () {
    return this.hsaStore.totalEmployeePersonalContributions() + this.projectedPersonalScheduledContributions;
  }

  get employeeContributionsTotal (): number {
    if (this.userStore.employeeFundingOption === FundingOption.BendCalculatePull) {
      return this.totalEmployeePayrollContributions +
          this.employeePersonalContributions + this.hsaStore.bankDirectContributions(this.activePayrollYear);
    }
    return this.hsaStore.employeePreTaxContributions(this.activePayrollYear)
        + this.employeePersonalContributions
        + this.hsaStore.bankDirectContributions(this.activePayrollYear)
        + this.hsaStore.employeePostTaxContributions(this.activePayrollYear);
  }

  get employerTotal (): number {
    if (this.userStore.employeeFundingOption === FundingOption.BendCalculatePull) {
      return this.hsaTotalStore.employerAmount;
    }
    return this.hsaStore.totalEmployerPayrollContributions(this.activePayrollYear);
  }

  get projectedPersonalScheduledContributions (): number {
    return this.hsaTotalStore.personalScheduledContributionsByYear(moment().year(), true);
  }

  get otherHsaAmount () {
    return this.hsaLimitStore.otherHsaAmount();
  }

  get hasScheduledContributions (): boolean {
    return this.formModel.personalContributionScheduleId > 0 && this.formModel.status ===
        ContributionScheduleStatus.Active;
  }

  get showAddButton (): boolean {
    return !this.hasScheduledContributions && !this.isEditing && !this.userStore.isEmployeeAccountType;
  }

  get showEditButton (): boolean {
    return this.hasScheduledContributions && !this.isEditing;
  }

  get showSaveButton (): boolean {
    return this.isEditing;
  }

  get showStopLink (): boolean {
    return this.hasScheduledContributions && !this.isEditing;
  }

  get linkedAccountSelectOptions () {
    if (!this.bankAccountStore.linkedAccountsLoaded) {
      return [];
    }

    const verifiedAccountList = this.bankAccountStore.sortedLinkedContributionAccounts(
        "defaultScheduledContributionAccountId").map(account => ({
      text: account.bankAccountName + " " + account.bankAccountNumber,
      value: account.linkedBankAccountId
    }));

    return [
      ...verifiedAccountList
    ];
  }

  get selectedLinkedAccountLastFourDigits (): string {
    const selectedAccount = _.find(this.bankAccountStore.verifiedLinkedAccounts,
        x => x.linkedBankAccountId === this.defaultScheduledContributionAccountId);

    if (!selectedAccount) {
      return "";
    }

    return `${selectedAccount.bankAccountNumber}`;
  }

  get submitDisabled (): boolean {
    return this.$validator.errors.any() || this.isSubmitting;
  }

  onCancelSubmit (): void {
    this.confirmModalVisible = false;
    this.cancelModalVisible = false;
  }

  showConfirmModal (): void {
    this.$validator.validateAll()
        .then(isValid => {
          if (isValid) {
            this.confirmModalVisible = true;
          }
        });
  }

  showCancelModal (): void {
    this.cancelModalVisible = true;
  }

  onAddClick (): void {
    this.isEditing = true;
  }

  onEditClick (): void {
    this.isEditing = true;
  }

  onCancelEditClick (): void {
    this.isEditing = false;
  }

  onSaveClick (): void {
    this.onSubmit();
  }

  onSubmitCancellation (): void {
    this.isSubmitting = true;
    this.formModel.status = ContributionScheduleStatus.Cancelled;
    this.scheduledPersonalContributionStore.saveScheduledPersonalContribution(this.formModel)
        .then(() => {
          this.formModel = {
            ...emptyScheduledPersonalContribution,
            personalContributionScheduleId: this.formModel.personalContributionScheduleId,
            status: ContributionScheduleStatus.Cancelled
          };
          this.isSubmitting = false;
          this.cancelModalVisible = false;
        });
  }

  onSubmit (): void {
    this.$validator.validateAll()
        .then(isValid => {
          if (!isValid) {
            this.isSubmitting = false;
            this.confirmModalVisible = false;
            return;
          }

          this.isSubmitting = true;
          if (this.bankAccountStore.defaultScheduledContributionAccount === undefined ||
              this.defaultScheduledContributionAccountId !==
              this.bankAccountStore.defaultScheduledContributionAccount.linkedBankAccountId) {
            const payload: BankAccountDefaultsRequest = {
              defaultContributionAccountId: null,
              defaultReimbursementAccountId: null,
              defaultScheduledContributionAccountId: this.defaultScheduledContributionAccountId
            };

            this.bankAccountStore.updateDefaultAccounts(payload);
          }
          this.formModel.status = ContributionScheduleStatus.Active;
          this.scheduledPersonalContributionStore.saveScheduledPersonalContribution(this.formModel)
              .then(() => {
                this.isSubmitting = false;
                this.confirmModalVisible = false;
                this.isEditing = false;
              });
        });
  }
}
